import React, { useEffect, useMemo, useRef, useState } from "react";
import { RichTextLexicalEditor } from "./RichTextLexicalEditor.js";
import {
  getRichTextModelPlainText,
  legacyMarkupLinesToRichTextModel,
  normalizeRichTextModel,
  richTextModelToLegacyMarkupLines,
} from "../../../../../../engine/modules/ui/richLabelModel.js";


function getInitialRichTextModel(text = [], richText) {
  if (richText && richText.version) {
    return normalizeRichTextModel(richText);
  }

  return legacyMarkupLinesToRichTextModel(text);
}

function getModelSignature(model) {
  return JSON.stringify(normalizeRichTextModel(model));
}

function TextPanel(props) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div
        className="EditorDrawerItemHeader"
        onClick={() => setOpen(!open)}
      >
        <span className="PanelOpenStatus">{open ? "▾" : "▸"}</span>
        <span>Text</span>
      </div>
      {open ? <TextDrawer {...props} /> : null}
    </div>
  );
}

function TextDrawer(props) {
  const text = Array.isArray(props.text) ? props.text : [];
  const propsModel = useMemo(() => getInitialRichTextModel(text, props.richText), [props.richText, text]);
  const propsModelSignature = useMemo(() => getModelSignature(propsModel), [propsModel]);
  const [editorModel, setEditorModel] = useState(propsModel);
  const [editorModelKey, setEditorModelKey] = useState(propsModelSignature);
  const lastSavedSignatureRef = useRef(propsModelSignature);
  const labelIdRef = useRef(props.id);

  useEffect(() => {
    const labelChanged = labelIdRef.current !== props.id;
    const externalChange = propsModelSignature !== lastSavedSignatureRef.current;

    if (labelChanged || externalChange) {
      labelIdRef.current = props.id;
      lastSavedSignatureRef.current = propsModelSignature;
      setEditorModel(propsModel);
      setEditorModelKey(`${props.id}:${propsModelSignature}`);
    }
  }, [props.id, propsModel, propsModelSignature]);

  const saveModel = (nextModel) => {
    const normalized = normalizeRichTextModel(nextModel);
    const nextSignature = getModelSignature(normalized);

    setEditorModel(normalized);

    if (nextSignature === lastSavedSignatureRef.current) {
      return;
    }

    lastSavedSignatureRef.current = nextSignature;
    const legacyLines = richTextModelToLegacyMarkupLines(normalized);

    if (typeof Inventum === "undefined" || !Inventum.labels) {
      return;
    }

    if (Inventum.labels.replaceText) {
      Inventum.labels.replaceText(props.id, legacyLines, normalized);
    } else {
      legacyLines.forEach((line, index) => {
        if (index < text.length) {
          Inventum.labels.editText(props.id, index, line);
        } else {
          Inventum.labels.addText(props.id, line);
        }
      });
    }

    props.getLabels();
  };

  return (
    <div className="TextDrawerContainer">
      <RichTextLexicalEditor
        model={editorModel}
        modelKey={editorModelKey}
        onChange={saveModel}
        style={props.style}
        onStyleChange={(styleUpdate, options) => updateLabelStyle(props, styleUpdate, options)}
      />
      <div className="TextDrawerHelpText">
        enter/return creates a new label text line
      </div>
    </div>
  );
}


function updateLabelStyle(props, styleUpdate, options = {}) {
  if (typeof Inventum === "undefined" || !Inventum.labels) {
    return;
  }

  Inventum.labels.setLabelStyle(props.id, styleUpdate);
  if (options.refresh !== false) {
    props.getLabels();
  }
}

export {
  TextPanel,
  getInitialRichTextModel,
  getRichTextModelPlainText,
  richTextModelToLegacyMarkupLines,
};
